import * as React from 'react';

import { useHistory } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'app';
import { LazyLoadingPage } from 'components';
import ErrorAccessDomain from 'components/Error/ErrorAccessDomain';
import ErrorAuth from 'components/Error/ErrorAuth';
import ErrorDomainNotFound from 'components/Error/ErrorDomainNotFound';
import {
  refreshAccessToken,
  selectAccount,
  selectAppDevice,
  selectAppState,
  selectAuth,
  updateDomainNotFound,
} from 'slice';
import { handleRedirectSignIn } from 'utils';

const WithAuthenticated = WrappedComponent =>
  function HOC(props) {
    const history = useHistory();
    const dispatch = useAppDispatch();
    const appDevice = useAppSelector(selectAppDevice);
    const auth = useAppSelector(selectAuth);
    const app = useAppSelector(selectAppState);
    const account = useAppSelector(selectAccount);

    React.useEffect(() => {
      const handleInitLoad = async () => {
        if (auth.canInit) {
          try {
            await dispatch(refreshAccessToken()).unwrap();
          } catch (error: any) {
            if (
              error?.message == 'Domain not found' ||
              error?.message == 'Invalid access domain'
            ) {
              if (error?.message == 'Domain not found') {
                dispatch(updateDomainNotFound(true));
              }
              if (error?.message == 'Invalid access domain') {
                dispatch(updateDomainNotFound(false));
              }
            }
            // handleRedirectPopupSignIn(router, '/sign-in');
          }
        }
      };

      if (auth.canInit && !auth.isInitAuth) {
        handleInitLoad();
      }
    }, [auth.canInit, auth.isInitAuth, dispatch]);

    const handleSilentRefreshToken = React.useCallback(async () => {
      if (auth.canInit) {
        try {
          await dispatch(refreshAccessToken()).unwrap();
        } catch (error: any) {
          if (
            error?.message == 'Domain not found' ||
            error?.message == 'Invalid access domain'
          ) {
            if (error?.message == 'Domain not found') {
              dispatch(updateDomainNotFound(true));
            }
            if (error?.message == 'Invalid access domain') {
              dispatch(updateDomainNotFound(false));
            }
          }

          // handleRedirectPopupSignIn(router, '/sign-in');
        }
      }
    }, [auth.canInit, dispatch]);

    React.useEffect(() => {
      if (auth.isInitAuth) {
        if (auth.isLogout) {
          if (!appDevice.isFirstLoad && !app.isLoading) {
            setTimeout(() => {
              handleRedirectSignIn(
                history,
                '/sign-in',
                appDevice.isSpecificBrowser,
                app?.domains?.hubIdentity?.domain
              );
            }, 0);
          }
        } else if (!auth.isAuthenticated && !auth.isLogout) {
          handleSilentRefreshToken();
        }
      }

      // Refresh silently every 9.5 minutes
      const intervalRefreshToken = setInterval(
        handleSilentRefreshToken,
        1000 * 60 * 9.5
      );

      return () => {
        clearInterval(intervalRefreshToken);
      };
    }, [
      history,
      app.isLoading,
      auth.isAuthenticated,
      auth.isLogout,
      auth.isInitAuth,
      appDevice.isFirstLoad,
      appDevice.isSpecificBrowser,
      handleSilentRefreshToken,
    ]);

    if (account.isInvalidAccessDomain) {
      return <ErrorAccessDomain />;
    }

    if (account.isDomainNotFound) {
      return <ErrorDomainNotFound />;
    }

    return (
      <>
        {!auth.isInitAuth ? <LazyLoadingPage /> : null}

        {auth.isInitAuth &&
        !auth.isAuthenticated &&
        !account.isDomainNotFound &&
        !account.isInvalidAccessDomain ? (
          <ErrorAuth />
        ) : null}

        {auth.isInitAuth &&
        auth.isAuthenticated &&
        !account.isInvalidAccessDomain ? (
          <WrappedComponent {...props} />
        ) : null}
      </>
    );
  };

export default WithAuthenticated;
